import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import StartBuildingButton from "../components/StartBuildingButton";

// Plain-language Privacy Policy aligned with PIPEDA (Canada's Personal
// Information Protection and Electronic Documents Act) and written so an
// Ontario teacher or school-board privacy officer can scan it in under
// two minutes. The page reflects the actual data flow today — no
// hypotheticals. Update whenever a new integration touches student or
// teacher data.
export default function Privacy() {
  const lastUpdated = "May 23, 2026";
  return (
    <div className="min-h-screen bg-[#f4f5f7] text-[#1c2d3d]">
      <header className="bg-[#1c2d3d] text-white">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-extrabold tracking-tight hover:opacity-80"
            data-testid="privacy-home-link"
          >
            <img
              src="/brand/atomicedu-logo-mark.png"
              alt="Atomic Edu"
              className="h-9 w-9 object-contain"
            />
            Atomic Edu
          </Link>
          <div className="flex items-center gap-2">
            <StartBuildingButton />
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm font-bold text-white/85 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12" data-testid="privacy-page">
        <div className="flex items-center gap-3 mb-3">
          <ShieldCheck className="h-7 w-7 text-[#4a6984]" />
          <p className="font-bold uppercase text-xs tracking-[0.22em] text-[#4a6984]/80">
            Privacy Policy
          </p>
        </div>
        <h1 className="font-black text-4xl sm:text-5xl text-[#4a6984] tracking-tight">
          What we collect, why, and your rights.
        </h1>
        <p className="mt-4 text-[#4a6984]/80 font-medium">
          Aligned with Canada's <strong>PIPEDA</strong> (Personal Information
          Protection and Electronic Documents Act) and written for Ontario
          teachers. Last updated <strong>{lastUpdated}</strong>.
        </p>

        <Section title="Who runs Atomic Edu">
          <p>
            Atomic Edu is a Canadian classroom-planning tool built for Ontario
            K–12 teachers. Questions, requests, or complaints? Email{" "}
            <a className="ae-link" href="mailto:privacy@atomicedu.ca">
              privacy@atomicedu.ca
            </a>
            . We respond within 30 days, the PIPEDA standard.
          </p>
        </Section>

        <Section title="What we collect">
          <ul>
            <li>
              <strong>Account information</strong> — your email, full name,
              and Google profile picture, only if you sign in with Google.
            </li>
            <li>
              <strong>Classroom profile</strong> — the grade levels and
              subjects you tell us you teach (used to filter the Tracker to
              your real classroom).
            </li>
            <li>
              <strong>Lesson inputs</strong> — anything you type into the
              generator (grade, subject, time, and the "Anything specific?"
              prompt). If you use "Build from Resource," we read the text
              you provide only to generate your lesson — it's processed in
              the moment and never stored. The finished lesson is saved to
              your library; the source material you uploaded is not.
            </li>
            <li>
              <strong>Generated activities</strong> — the lesson plans,
              teacher scripts, slide decks, and exports we create for you.
              These are saved to your library so you can re-open them later.
            </li>
            <li>
              <strong>Session and usage</strong> — login session token (an
              httpOnly cookie), and basic technical logs needed to keep the
              service running and to debug errors.
            </li>
          </ul>
          <p>
            <strong>We do NOT collect</strong> — and never want — any
            student personal information. Please do not paste a student's
            real name, photo, or identifying details into the prompt. The
            generator does not need it to build a great lesson.
          </p>
        </Section>

        <Section title="Google sign-in — what we can and cannot access">
          <p>
            Atomic Edu offers Google sign-in for convenience, plus an optional
            "Save to Google Drive" feature. Here's what we can and cannot do
            with your Google account.
          </p>
          <p>
            <strong>By default, when you sign in with Google we use the
            basic <em>profile</em> scope only.</strong> That means:
          </p>
          <ul>
            <li>
              We receive <strong>your email, your name, and your profile
              picture</strong> from Google — nothing else.
            </li>
            <li>
              We <strong>do not</strong> read your Gmail, your Google Calendar,
              your Contacts, your Classroom roster, or any other Google
              service. The OAuth scopes we request from Google do not grant
              that access, technically.
            </li>
            <li>
              We <strong>cannot</strong> post on your behalf, attach to your
              calendar, or send mail as you.
            </li>
          </ul>
          <p>
            <strong>"Save to Google Drive" is opt-in and uses a strictly
            limited scope.</strong> If — and only if — you click "Connect
            Google Drive" inside the app, we will ask Google to grant
            Atomic Edu the <code>drive.file</code> permission. This is the
            narrowest Drive permission Google offers:
          </p>
          <ul>
            <li>
              We can <strong>only create files that Atomic Edu itself
              uploads</strong> (your generated lessons, slide decks, and
              worksheets). They land in a folder called "Atomic Edu Lessons"
              in your Drive.
            </li>
            <li>
              We <strong>cannot read, edit, or delete any other file</strong>
              in your Drive. Files you created with another app are
              completely invisible to Atomic Edu — Google itself enforces
              this.
            </li>
            <li>
              You can revoke this access at any time from{" "}
              <a
                href="https://myaccount.google.com/permissions"
                target="_blank"
                rel="noreferrer"
                className="underline font-bold"
              >
                myaccount.google.com/permissions
              </a>{" "}
              or by clicking "Disconnect Google Drive" inside Atomic Edu.
            </li>
            <li>
              If you never click "Connect Google Drive", we never request
              this permission and we never touch your Drive in any way.
            </li>
          </ul>
        </Section>

        <Section title="Where your data lives">
          <ul>
            <li>
              <strong>Database</strong> — a MongoDB instance hosted in a
              Canadian / North-American data centre. Encrypted in transit
              (TLS) and at rest.
            </li>
            <li>
              <strong>Anthropic Claude</strong> — when you generate or
              refine a lesson, your prompt and the lesson context are sent
              to Anthropic's API for processing. Anthropic does not train
              on API inputs. Their privacy policy:{" "}
              <a className="ae-link" href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noreferrer">
                anthropic.com/legal/privacy
              </a>
              . If you upload a document using Build from Resource, that
              file is processed in memory to generate your lesson and is
              not stored — the resulting lesson is saved; the source
              file is not.
            </li>
            <li>
              <strong>Google (Gemini)</strong> — if you upload a file
              using "Build from Resource," the file's contents are sent
              to Google's Gemini AI to read and extract the text so we
              can generate your lesson. Atomic Edu does not store the
              uploaded file.
            </li>
            <li>
              <strong>No advertising trackers.</strong> We do not run Google
              Analytics, Facebook Pixel, ad networks, or any third-party
              tracking on this site.
            </li>
          </ul>
        </Section>

        <Section title="How long we keep it">
          <ul>
            <li>
              <strong>Generated activities</strong> — kept in your library
              until you delete them or until you delete your account.
            </li>
            <li>
              <strong>"Build from Resource" uploads</strong> — your
              uploaded content is processed in memory to build the lesson
              and is <strong>not stored</strong>. The resulting lesson is
              saved; the source you uploaded is not.
            </li>
            <li>
              <strong>Session tokens</strong> — expire after 7 days, or
              instantly when you sign out.
            </li>
            <li>
              <strong>Account deletion</strong> — when you delete your
              account (see below), all of your activities, sessions, and
              profile data are permanently removed. We cannot recover them
              afterwards.
            </li>
          </ul>
        </Section>

        <Section title="Your rights under PIPEDA">
          <ul>
            <li>
              <strong>Access</strong> — request a copy of the personal
              information we hold about you.
            </li>
            <li>
              <strong>Correction</strong> — update your name, classroom
              profile, or any other detail at any time from the Teacher
              Dashboard.
            </li>
            <li>
              <strong>Deletion</strong> — delete your entire account from
              your profile menu (one click). This wipes your activities,
              sessions, and user record. You can also delete individual
              lessons from your library.
            </li>
            <li>
              <strong>Withdraw consent</strong> — stop using the service or
              delete your account; we keep no shadow copy.
            </li>
            <li>
              <strong>Complain</strong> — to{" "}
              <a className="ae-link" href="mailto:privacy@atomicedu.ca">
                privacy@atomicedu.ca
              </a>{" "}
              first; if unresolved, to the Office of the Privacy
              Commissioner of Canada (
              <a className="ae-link" href="https://www.priv.gc.ca/" target="_blank" rel="noreferrer">
                priv.gc.ca
              </a>
              ).
            </li>
          </ul>
        </Section>

        <Section title="Children and student data">
          <p>
            Atomic Edu is a tool for teachers, not for students of any
            age. We don't create student accounts or ask students to sign
            in, and we don't knowingly collect personal information from
            children. If you believe a student has created an account,
            email us and we'll delete it right away.
          </p>
        </Section>

        <Section title="Changes to this policy">
          <p>
            We will post the new policy here and update the "Last updated"
            date at the top. Material changes (new integrations, new data
            collected) will also be announced inside the app the next time
            you sign in.
          </p>
        </Section>

        <div className="mt-12 pt-8 border-t border-[#4a6984]/15 text-sm text-[#4a6984]/70">
          See also: <Link className="ae-link" to="/terms">Terms of Service</Link>.
        </div>
      </main>
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-extrabold text-2xl text-[#4a6984] tracking-tight mb-3">
      {title}
    </h2>
    <div className="ae-prose text-[#1c2d3d]/90">{children}</div>
  </section>
);
